import Decimal from 'decimal.js';
import type { ISODate } from '@contracts/common';
import { addDays } from './dates';

/**
 * Pure Workplace tree rules of spec §4.4 (T1–T3, T5) and the fractional sibling position of §4.2. The database
 * guards enforce the same rules under `a_company_lock`; these functions exist so the service and the browser can
 * answer early. They are never the only enforcement. `HAS_CHILDREN` (T4) is a delete rule, not a placement rule,
 * and lives only in the database and the service.
 */
export type TreeItem = {
  id: string;
  parentId: string | null;
  itemType: 'task' | 'note';
  /** A task's status; a note carries none and is never open work. */
  status: 'not_started' | 'in_progress' | 'completed' | null;
  deletedAt: string | null;
};

export type TreeViolation = 'CYCLE' | 'NOTE_AT_ROOT' | 'DELETED_PARENT' | 'OPEN_CHILDREN' | 'TREE_DONE';

/** Spec §3 "việc đang mở": a live task whose status is not `completed`. */
const isOpenTask = (item: TreeItem): boolean =>
  item.deletedAt === null && item.itemType === 'task' && item.status !== 'completed';

const byId = (items: TreeItem[]): Map<string, TreeItem> => new Map(items.map((i) => [i.id, i]));

/**
 * Ids of every live open task strictly below `id`, at any depth and through notes. A deleted row is ignored and
 * nothing below it is walked. A visited set keeps corrupt cyclic data from looping.
 */
export function openDescendantIds(items: TreeItem[], id: string): string[] {
  const children = new Map<string, TreeItem[]>();
  for (const item of items) {
    if (item.parentId === null || item.deletedAt !== null) continue;
    const list = children.get(item.parentId) ?? [];
    list.push(item);
    children.set(item.parentId, list);
  }
  const open: string[] = [];
  const visited = new Set<string>([id]);
  const stack = [id];
  while (stack.length > 0) {
    for (const child of children.get(stack.pop()!) ?? []) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      if (isOpenTask(child)) open.push(child.id);
      stack.push(child.id);
    }
  }
  return open;
}

/**
 * Checks placing `itemId` (already present in `items` with its intended type and status) under `parentId`, or at
 * the root when `parentId` is null. Returns the first violated rule, or null when the placement is valid.
 */
export function validatePlacement(items: TreeItem[], itemId: string, parentId: string | null): TreeViolation | null {
  const index = byId(items);
  const item = index.get(itemId);
  if (item === undefined) throw new RangeError(`unknown item ${itemId}`);

  if (parentId === null) {
    if (item.itemType === 'note') return 'NOTE_AT_ROOT';
    // T5: a completed root may not carry open work — the detach case of spec §4.4.
    if (item.status === 'completed' && openDescendantIds(items, itemId).length > 0) return 'OPEN_CHILDREN';
    return null;
  }

  // T2: the parent is a live row of the same company (the caller passes only its company) and not the item itself.
  const parent = index.get(parentId);
  if (parentId === itemId || parent === undefined || parent.deletedAt !== null) return 'DELETED_PARENT';

  // T3: walk up from the new parent; meeting the item means the parent sits in the item's own branch.
  let rootOfTarget: TreeItem = parent;
  const visited = new Set<string>();
  for (let cursor: TreeItem | undefined = parent; cursor !== undefined; ) {
    if (cursor.id === itemId) return 'CYCLE';
    if (visited.has(cursor.id)) break;
    visited.add(cursor.id);
    rootOfTarget = cursor;
    cursor = cursor.parentId === null ? undefined : index.get(cursor.parentId);
  }

  // T5: a done tree holds no open task, neither the moved item nor anything in its branch.
  if (rootOfTarget.parentId === null && rootOfTarget.status === 'completed'
      && (isOpenTask(item) || openDescendantIds(items, itemId).length > 0)) return 'TREE_DONE';
  return null;
}

/** Spec §4.2: siblings closer than this are renumbered 1, 2, 3… in the same transaction. */
export const RENUMBER_GAP = '1e-9';

/**
 * Exact decimal arithmetic (a module-local clone, as in `finance.ts`). 60 halvings of a unit gap need about 61
 * significant digits; the default precision of 20 rounds the midpoint, and at large magnitudes rounds it onto a
 * neighbour, collapsing sibling order.
 */
const D = Decimal.clone({ precision: 80 });

/**
 * Position for an item inserted between two adjacent siblings (null = no sibling on that side). `renumber` is true
 * when the gap between the new position and its nearer neighbour — half the old gap, since it is the midpoint — is
 * below `RENUMBER_GAP`. Positions travel as canonical decimal strings, never in exponent notation.
 */
export function positionBetween(previous: string | null, next: string | null): { position: string; renumber: boolean } {
  if (previous === null && next === null) return { position: '1', renumber: false };
  if (next === null) return { position: new D(previous!).plus(1).toFixed(), renumber: false };
  if (previous === null) return { position: new D(next).minus(1).toFixed(), renumber: false };

  const low = new D(previous);
  const high = new D(next);
  if (low.greaterThan(high)) throw new RangeError(`sibling positions out of order: ${previous} > ${next}`);
  const middle = low.plus(high).div(2);
  return { position: middle.toFixed(), renumber: middle.minus(low).lessThan(RENUMBER_GAP) };
}

/**
 * Sibling order of spec §4.2: by position as an exact decimal, then by id. Positions are strings, and neither a
 * string compare ('10' < '9.5') nor a JS number (rounding past 15 digits) orders them correctly.
 */
export function comparePositions(a: { position: string; id: string }, b: { position: string; id: string }): number {
  const byPosition = new D(a.position).comparedTo(b.position);
  if (byPosition !== 0) return byPosition;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** What `effectiveProject` reads of one item: its parent and the project it names itself (null: none). */
export type ProjectLink<P> = { parentId: string | null; project: P | null };

/**
 * The project an item belongs to (PO 2026-09-28, 020): its root's — only a root names a project, every card below
 * follows it. `lookup` answers the items at hand (the board); one it does not know ends the walk with none, and cyclic
 * data cannot loop. The server answers the same with SQL (`effectiveProjectIdSql`, `inProjectSql` in apps/api
 * workplace/link-sql.ts).
 */
export function effectiveProject<P>(id: string, lookup: (id: string) => ProjectLink<P> | undefined): P | null {
  const seen = new Set<string>();
  for (let at: string = id; !seen.has(at);) {
    const item = lookup(at);
    if (item === undefined) return null;
    if (item.parentId === null) return item.project;
    seen.add(at);
    at = item.parentId;
  }
  return null;
}

/** One link of a card's chain for `inheritedAssignment`: a note carries no block. */
export type AssignmentLink<R> = { itemType: 'task' | 'note'; recipient: R | null; deadline: ISODate | null };

/**
 * The block a new task starts with (spec §6.4, §8.3.4, D194): the recipient of the nearest task in `chain` (parent
 * first, upwards), skipping notes, else the asker; always due `day + 30`. A child does not take its parent's deadline
 * (PO hotfix 2026-09-30): an overdue parent would hand every new child a date already past. The board and the
 * assistant both follow this one rule.
 */
export function inheritedAssignment<R>(chain: readonly AssignmentLink<R>[], me: R, day: ISODate):
  { recipient: R; deadline: ISODate } {
  const deadline = addDays(day, 30);
  for (const link of chain) {
    if (link.itemType === 'task' && link.recipient !== null) return { recipient: link.recipient, deadline };
  }
  return { recipient: me, deadline };
}
